import { api } from "@api";
import { zodResolver } from "@hookform/resolvers/zod";
import { Step6FormValues, step6Schema } from "@/schemas/onboarding-schema";
import { Loader2, X } from "lucide-react";
import { useState, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { fr } from "date-fns/locale";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormLabel } from "@/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import FormShell from "@/components/form-shell";
import { OrgSelection } from "@/components/org-selection";
import { useOnboarding } from "@/hooks/useOnboarding";
import { format } from "date-fns";

export function OnboardingStep6() {
  const {
    organizations,
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection,
    updateOnboardingStep,
  } = useOnboarding("step6");

  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());

  // Helper function pour créer les queries de préchargement
  const getMonthQueries = (baseMonth: Date) => {
    const prevMonth = new Date(baseMonth.getFullYear(), baseMonth.getMonth() - 1, 1);
    const nextMonth = new Date(baseMonth.getFullYear(), baseMonth.getMonth() + 1, 1);

    return [prevMonth, baseMonth, nextMonth].map((date) => ({
      year: date.getFullYear().toString(),
      month: (date.getMonth() + 1).toString(),
    }));
  };

  // Récupération des périodes d'ouverture pour le mois sélectionné et les mois adjacents
  const queries = getMonthQueries(selectedMonth);

  const currentQuery = {
    year: selectedMonth.getFullYear().toString(),
    month: (selectedMonth.getMonth() + 1).toString(),
  };

  const {
    data: openingData,
    isLoading: isLoadingOpeningPeriods,
    error: openingPeriodsError,
  } = api.useQuery("get", "/onboarding/opening-periods/{year}/{month}", {
    params: {
      path: currentQuery,
    },
    staleTime: 60 * 1000, // 60 secondes
  });

  // Préchargement des mois adjacents
  const prevMonthQuery = queries[0];
  const nextMonthQuery = queries[2];

  const { data: prevOpeningData } = api.useQuery("get", "/onboarding/opening-periods/{year}/{month}", {
    params: {
      path: prevMonthQuery,
    },
    staleTime: 60 * 1000,
  });

  const { data: nextOpeningData } = api.useQuery("get", "/onboarding/opening-periods/{year}/{month}", {
    params: {
      path: nextMonthQuery,
    },
    staleTime: 60 * 1000,
  });

  // Mutation pour sauvegarder les fermetures
  const { mutate: saveClosingDates, isPending: isSavingClosing } = api.useMutation("post", "/onboarding/closing", {
    onSuccess: () => {
      toast.success("Fermetures exceptionnelles sauvegardées avec succès!");
      updateOnboardingStep("step7");
      navigate("/onboarding/step7");
    },
    onError: (error) => {
      console.error("Error saving closing dates:", error);
      toast.error("Erreur lors de la sauvegarde", {
        description: error.message || "Impossible de sauvegarder les fermetures exceptionnelles",
      });
    },
  });

  const form = useForm<Step6FormValues>({
    resolver: zodResolver(step6Schema),
    defaultValues: {
      closureDates: [],
      closurePeriods: [],
    },
  });

  const closurePeriods = useWatch({ control: form.control, name: "closurePeriods" });

  // Données d'ouverture memoized avec cache
  const { openDays, availablePeriods } = useMemo(() => {
    const combineDays = (a?: string[] | null, b?: string[] | null, c?: string[] | null): string[] => [...new Set([...(a || []), ...(b || []), ...(c || [])])];

    const combinePeriods = (
      a?: Record<string, string[]> | null,
      b?: Record<string, string[]> | null,
      c?: Record<string, string[]> | null
    ): Record<string, string[]> => ({ ...(a || {}), ...(b || {}), ...(c || {}) });

    return {
      openDays: combineDays(openingData?.openDays, prevOpeningData?.openDays, nextOpeningData?.openDays),
      availablePeriods: combinePeriods(
        (openingData?.availablePeriods as Record<string, string[]>) || {},
        (prevOpeningData?.availablePeriods as Record<string, string[]>) || {},
        (nextOpeningData?.availablePeriods as Record<string, string[]>) || {}
      ),
    };
  }, [openingData, prevOpeningData, nextOpeningData]);

  // Fonction pour récupérer les périodes disponibles d'une date, même si elle n'est pas dans le mois courant
  const getAvailablePeriodsForDate = (date: Date): string[] => {
    const dateKey = date.toISOString().split("T")[0];
    return availablePeriods[dateKey] || [];
  };

  const onSubmit = (data: Step6FormValues) => {
    saveClosingDates({ body: data });
  };

  const handlePeriodChange = (date: Date, period: "matin" | "midi" | "apres-midi" | "journee") => {
    const dateStr = date.toISOString();
    const existingPeriodIndex = closurePeriods.findIndex((p) => new Date(p.date).toDateString() === date.toDateString());

    // Récupérer les périodes disponibles pour cette date
    const dayAvailablePeriods = getAvailablePeriodsForDate(date);

    // Type guard pour vérifier que dayAvailablePeriods est un tableau de strings
    if (!Array.isArray(dayAvailablePeriods)) {
      return;
    }

    // Handle the special "journee" case
    const isJournee = period === "journee";

    if (existingPeriodIndex !== -1) {
      // Si la période existe déjà pour cette date
      const currentPeriods = closurePeriods[existingPeriodIndex].periods;
      let newPeriods: ("matin" | "midi" | "apres-midi" | "journee")[];

      if (isJournee) {
        // Si on sélectionne/désélectionne "journee", on ajuste toutes les autres périodes
        if (currentPeriods.includes("journee")) {
          // Si on désélectionne journée, on retire toutes les périodes
          newPeriods = [];
        } else {
          // Si on sélectionne journée, on ajoute toutes les périodes disponibles
          newPeriods = [...(Array.isArray(dayAvailablePeriods) ? dayAvailablePeriods.filter((p: string) => p !== "journee") : []), "journee"] as (
            | "matin"
            | "midi"
            | "apres-midi"
            | "journee"
          )[];
        }
      } else {
        // Gestion normale pour les autres périodes - uniquement si la période est disponible
        if (Array.isArray(dayAvailablePeriods) && dayAvailablePeriods.includes(period)) {
          newPeriods = currentPeriods.includes(period as "matin" | "midi" | "apres-midi" | "journee")
            ? currentPeriods.filter((p) => p !== period)
            : [...currentPeriods, period as "matin" | "midi" | "apres-midi" | "journee"];

          // Si on a coché toutes les périodes disponibles, on coche aussi journee
          const newPeriodsSet = new Set(newPeriods.filter((p) => p !== "journee"));
          const hasAllAvailable = dayAvailablePeriods.every((p: string) => newPeriodsSet.has(p as "matin" | "midi" | "apres-midi"));

          if (hasAllAvailable && !newPeriods.includes("journee")) {
            newPeriods.push("journee");
          }
          // Si on a décoché une période mais journee est cochée, on décoche journee
          if (currentPeriods.includes("journee") && !hasAllAvailable) {
            newPeriods = newPeriods.filter((p) => p !== "journee");
          }
        } else {
          // Période non disponible, ne pas modifier
          return;
        }
      }

      const updatedPeriods = [...closurePeriods];
      updatedPeriods[existingPeriodIndex] = {
        ...closurePeriods[existingPeriodIndex],
        periods: newPeriods,
      };

      form.setValue("closurePeriods", updatedPeriods);
    } else {
      // Si la période n'existe pas encore pour cette date
      // Par défaut, on sélectionne toutes les périodes disponibles plus "journee"
      const defaultPeriods: ("matin" | "midi" | "apres-midi" | "journee")[] = [
        ...(Array.isArray(dayAvailablePeriods) ? dayAvailablePeriods : []),
        "journee",
      ] as ("matin" | "midi" | "apres-midi" | "journee")[];
      form.setValue("closurePeriods", [...closurePeriods, { date: dateStr, periods: defaultPeriods }]);
    }
  };

  const isPeriodSelected = (date: Date, period: "matin" | "midi" | "apres-midi" | "journee") => {
    const periodData = closurePeriods.find((p) => new Date(p.date).toDateString() === date.toDateString());
    return periodData?.periods.includes(period) || false;
  };

  const removeDateFromClosure = (dateToRemove: Date) => {
    const updatedPeriods = closurePeriods.filter((period) => new Date(period.date).toDateString() !== dateToRemove.toDateString());
    form.setValue("closurePeriods", updatedPeriods);
  };

  const getSelectedPeriodsText = (date: Date): string => {
    const periodData = closurePeriods.find((p) => new Date(p.date).toDateString() === date.toDateString());
    if (!periodData || periodData.periods.length === 0) return "Aucune période sélectionnée";

    // Si journée complète est sélectionnée, n'afficher que ça
    if (periodData.periods.includes("journee")) {
      return "Fermé toute la journée";
    }

    // Définir l'ordre et les labels avec articles appropriés
    const periodOrder = ["matin", "midi", "apres-midi"];
    const periodLabels = {
      matin: { article: "le ", label: "matin" },
      midi: { article: "le ", label: "midi" },
      "apres-midi": { article: "l'", label: "après-midi" },
    };

    // Trier les périodes selon l'ordre défini et créer les textes avec articles
    const sortedPeriods = periodData.periods
      .filter((p) => p !== "journee")
      .sort((a, b) => periodOrder.indexOf(a) - periodOrder.indexOf(b))
      .map((period) => {
        const config = periodLabels[period as keyof typeof periodLabels];
        return config ? `${config.article}${config.label}` : period;
      });

    if (sortedPeriods.length === 0) {
      return "Aucune période sélectionnée";
    }

    if (sortedPeriods.length === 1) {
      return `Fermé ${sortedPeriods[0]}`;
    }

    // Pour plusieurs périodes, utiliser la virgule et "et" pour la dernière
    const lastPeriod = sortedPeriods.pop();
    return `Fermé ${sortedPeriods.join(", ")} et ${lastPeriod}`;
  };

  // Gestion des erreurs de chargement
  if (openingPeriodsError) {
    return (
      <FormShell title="Fermetures exceptionnelles" subtitle="Indiquez les jours de fermeture de votre club" onSubmit={() => {}}>
        <div className="flex flex-col items-center justify-center p-8">
          <p className="mb-4 text-red-600">Erreur lors du chargement des périodes d'ouverture</p>
          <Button onClick={() => window.location.reload()}>Réessayer</Button>
        </div>
      </FormShell>
    );
  }

  return (
    <>
      <FormShell title="Fermetures exceptionnelles" subtitle="Indiquez les jours de fermeture de votre club" onSubmit={form.handleSubmit(onSubmit)}>
        <Form {...form}>
          <div className="flex w-full flex-col gap-8">
            <div className="gap-4">
              <div className="relative flex flex-col items-center justify-center">
                <Calendar
                  mode="multiple"
                  selected={closurePeriods.map((p) => new Date(p.date))}
                  onSelect={(dates) => {
                    // Gérer la sélection multiple ici
                    if (!dates || dates.length === 0) {
                      form.setValue("closurePeriods", []);
                      return;
                    }

                    // Filtrer seulement les dates qui sont dans les jours d'ouverture
                    const validDates = dates.filter((date) => {
                      const dateKey = date.toISOString().split("T")[0];
                      return openDays.includes(dateKey);
                    });

                    // Trouver les nouvelles dates ajoutées
                    const currentDates = closurePeriods.map((p) => new Date(p.date).toDateString());
                    const newDates = validDates.filter((d) => !currentDates.includes(d.toDateString()));

                    // Ajouter les nouvelles dates avec toutes les périodes disponibles par défaut
                    const newPeriods = [
                      ...closurePeriods,
                      ...newDates.map((date) => {
                        const dayAvailablePeriods = getAvailablePeriodsForDate(date);
                        return {
                          date: date.toISOString(),
                          periods: [...(Array.isArray(dayAvailablePeriods) ? dayAvailablePeriods : []), "journee"] as (
                            | "matin"
                            | "midi"
                            | "apres-midi"
                            | "journee"
                          )[],
                        };
                      }),
                    ];

                    // Filtrer pour ne garder que les dates sélectionnées
                    const selectedDateStrings = validDates.map((d) => d.toDateString());
                    const filteredPeriods = newPeriods.filter((period) => selectedDateStrings.includes(new Date(period.date).toDateString()));

                    form.setValue("closurePeriods", filteredPeriods);
                  }}
                  month={selectedMonth}
                  onMonthChange={setSelectedMonth}
                  className="rounded-md border"
                  disabled={(date) => {
                    // Désactiver les jours où le club n'est pas ouvert
                    const dateKey = date.toISOString().split("T")[0];
                    return !openDays.includes(dateKey) || isLoadingOpeningPeriods;
                  }}
                  locale={fr}
                />
                {/* Overlay de chargement */}
                {isLoadingOpeningPeriods && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-md bg-white/80 backdrop-blur-sm">
                    <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-lg">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Chargement...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Liste des jours fermés */}
              <div className="mt-6">
                <FormLabel className="text-md mb-2 font-bold">Jours de fermeture sélectionnés</FormLabel>
                {closurePeriods.length === 0 ? (
                  <FormLabel className="text-sm font-normal text-muted-foreground">Aucun jour de fermeture sélectionné</FormLabel>
                ) : (
                  <div className="space-y-2">
                    {closurePeriods.map((periodData, index) => {
                      const date = new Date(periodData.date);
                      const dayAvailablePeriods = getAvailablePeriodsForDate(date);

                      return (
                        <div key={index} className="flex items-center justify-between rounded-md border p-3">
                          <div>
                            <span className="text-sm font-medium">{format(date, "EE dd MMMM yyyy", { locale: fr })}</span>
                            <div className="text-sm font-normal text-muted-foreground">{getSelectedPeriodsText(date)}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" size="sm">
                                  Horaire
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80">
                                <div className="space-y-4">
                                  <FormLabel className="text-sm">Fermé le {format(date, "EEEE dd MMMM yyyy", { locale: fr })} durant :</FormLabel>
                                  <div className="grid gap-3">
                                    {Array.isArray(dayAvailablePeriods) &&
                                      dayAvailablePeriods.map((period: string) => (
                                        <div key={period} className="flex items-center gap-2">
                                          <Checkbox
                                            id={`${period}-${index}`}
                                            checked={isPeriodSelected(date, period as "matin" | "midi" | "apres-midi")}
                                            onCheckedChange={() => handlePeriodChange(date, period as "matin" | "midi" | "apres-midi")}
                                          />
                                          <FormLabel
                                            htmlFor={`${period}-${index}`}
                                            className={`hover:cursor-pointer ${isPeriodSelected(date, "journee") ? "text-muted-foreground line-through" : ""}`}
                                          >
                                            {period === "matin" ? "Matin" : period === "midi" ? "Midi" : period === "apres-midi" ? "Après-midi" : period}
                                          </FormLabel>
                                        </div>
                                      ))}
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        id={`journee-${index}`}
                                        checked={isPeriodSelected(date, "journee")}
                                        onCheckedChange={() => handlePeriodChange(date, "journee")}
                                      />
                                      <FormLabel htmlFor={`journee-${index}`} className="hover:cursor-pointer">
                                        Journée complète
                                      </FormLabel>
                                    </div>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeDateFromClosure(date)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <Button type="submit" className="mt-4 flex w-full items-center justify-center" disabled={isSavingClosing || isLoadingOpeningPeriods}>
              {isSavingClosing ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : "Continuer"}
            </Button>
          </div>
        </Form>
      </FormShell>

      {/* Organisation selection dialog */}
      <OrgSelection
        open={showOrgSelection}
        organizations={organizations || []}
        selectedOrgId={selectedOrgId}
        onSelectedOrgChange={setSelectedOrgId}
        onConfirm={() => handleSetActiveOrganization(selectedOrgId)}
        onClose={onCloseOrgSelection}
        isLoading={isSettingActive}
      />
    </>
  );
}
